import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon, Edit as EditIcon, LocalShipping as LocalShippingIcon, MoneyOff as MoneyOffIcon, RestartAlt as RestartAltIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

type DeliveryTransactionRow = {
  conversation_id: number;
  message_id: number;
  created_at: string;
  stage: 'offer' | 'checkout_pending' | 'paid' | 'failed' | 'unknown';
  has_offer: boolean;
  has_payment: boolean;
  pending_checkout_count: number;
  stripe_session_id: string | null;
  amount_eur: number | null;
  paid_amount_eur?: number | null;
  proposed_amount_eur?: number | null;
  refund_status: 'none' | 'partial' | 'full';
  refunded_amount_eur: number | null;
  tracking: string | null;
  is_returned?: boolean | null;
  returned_at?: string | null;
  return_label?: string | null;
  is_delivered?: boolean | null;
  delivered_at?: string | null;
  delivered_label?: string | null;
  parcel_status?: string | null;
  etiquette_url: string | null;
  shipping?: {
    mode?: 'home' | 'pickup_point' | null;
    address?: string | null;
    pickup_point?: string | null;
    phone?: string | null;
  } | null;
  payer: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string | null;
    address?: string | null;
    postal_code?: string | null;
    city?: string | null;
  };
  finder: { first_name?: string; last_name?: string; email?: string };
  announcement: { title?: string; type?: string };
  announcement_thumbnail_url: string | null;
  found_object: { id: number; status: string; reference_code?: string | null } | null;
};

export default function StripeDashboardPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [trackingSearchInput, setTrackingSearchInput] = useState('');
  const [trackingSearch, setTrackingSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingToShow, setTrackingToShow] = useState<string | null>(null);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<DeliveryTransactionRow | null>(null);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConversationId, setResetConversationId] = useState('');
  const [resetScope, setResetScope] = useState<'full' | 'payment_only'>('full');
  const [resetTargetConversationId, setResetTargetConversationId] = useState<number | null>(null);

  const queryClient = useQueryClient();

  const [stripeMode, setStripeMode] = useState<'test' | 'live'>('test');
  const [stripeSettingsMessage, setStripeSettingsMessage] = useState<string | null>(null);
  const [stripeSettingsError, setStripeSettingsError] = useState<string | null>(null);

  const stripeSettingsQuery = useQuery({
    queryKey: ['stripe-settings'],
    queryFn: () => apiService.getStripeSettings(),
  });

  React.useEffect(() => {
    const mode = stripeSettingsQuery.data?.settings?.mode;
    if (mode === 'test' || mode === 'live') {
      setStripeMode(mode);
    }
  }, [stripeSettingsQuery.data?.settings?.mode]);

  const stripeSettingsMutation = useMutation({
    mutationFn: (mode: 'test' | 'live') => apiService.updateStripeSettings({ mode }),
    onSuccess: (res) => {
      setStripeSettingsError(null);
      setStripeSettingsMessage(res.message || 'Mode Stripe mis à jour.');
      setStripeMode(res.settings.mode);
      queryClient.invalidateQueries({ queryKey: ['stripe-settings'] });
      window.setTimeout(() => setStripeSettingsMessage(null), 3000);
    },
    onError: (err: unknown) => {
      const apiErr = err as { response?: { data?: { error?: string } } };
      setStripeSettingsError(apiErr?.response?.data?.error || 'Impossible de changer le mode Stripe.');
    },
  });

  const stripeSettings = stripeSettingsQuery.data?.settings;

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['delivery-transactions', page, limit, trackingSearch, statusFilter],
    queryFn: () =>
      apiService.getDeliveryTransactions({
        page,
        limit,
        tracking: trackingSearch || undefined,
        status: statusFilter || undefined,
      }),
  });

  const trackingQuery = useQuery({
    queryKey: ['colissimo-tracking', trackingToShow],
    queryFn: () => apiService.getColissimoTracking({ tracking: trackingToShow as string }),
    enabled: !!trackingToShow,
    retry: false,
  });

  const resetStatusQuery = useQuery({
    queryKey: ['delivery-reset-status', resetTargetConversationId],
    queryFn: () => apiService.getDeliveryConversationStatus(resetTargetConversationId as number),
    enabled: resetDialogOpen && resetTargetConversationId != null && resetTargetConversationId > 0,
    retry: false,
  });

  const [resetSuccessMessage, setResetSuccessMessage] = useState<string | null>(null);

  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState<DeliveryTransactionRow | null>(null);

  const [amountDialogOpen, setAmountDialogOpen] = useState(false);
  const [amountTarget, setAmountTarget] = useState<DeliveryTransactionRow | null>(null);
  const [amountEurInput, setAmountEurInput] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [amountSuccess, setAmountSuccess] = useState<string | null>(null);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [refundSuccessMessage, setRefundSuccessMessage] = useState<string | null>(null);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [regenerateSuccessMessage, setRegenerateSuccessMessage] = useState<string | null>(null);

  const resetMutation = useMutation({
    mutationFn: (params: { conversationId: number; scope: 'full' | 'payment_only' }) =>
      apiService.resetDeliveryConversation(params.conversationId, params.scope),
    onSuccess: (data) => {
      const d = data.result.deleted;
      setResetSuccessMessage(
        `Conversation #${data.result.conversation_id} réinitialisée (${d.offer} proposition, ${d.payment} paiement(s), ${d.pending} checkout(s) supprimé(s)).`
      );
      queryClient.invalidateQueries({ queryKey: ['delivery-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['stripe-payments'] });
      queryClient.invalidateQueries({ queryKey: ['delivery-reset-status'] });
      setResetDialogOpen(false);
      setResetTargetConversationId(null);
      setResetConversationId('');
    },
  });

  const refundMutation = useMutation({
    mutationFn: (params: { conversationId: number }) =>
      apiService.createStripeRefund({ conversation_id: params.conversationId }),
    onSuccess: (data) => {
      setRefundError(null);
      setRefundSuccessMessage(
        `${data.message} (${formatEUR(data.refund.amount_eur)} — ${data.refund.refund_id})`
      );
      queryClient.invalidateQueries({ queryKey: ['delivery-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['stripe-payments'] });
      setRefundDialogOpen(false);
      setRefundTarget(null);
      window.setTimeout(() => setRefundSuccessMessage(null), 5000);
    },
    onError: (err: unknown) => {
      const apiErr = err as { response?: { data?: { error?: string } } };
      setRefundError(apiErr?.response?.data?.error || 'Impossible d’effectuer le remboursement Stripe.');
    },
  });

  const openRefundDialog = (transaction: DeliveryTransactionRow) => {
    setRefundTarget(transaction);
    setRefundError(null);
    setRefundDialogOpen(true);
  };

  const openResetDialog = (conversationId: number) => {
    setResetTargetConversationId(conversationId);
    setResetScope('full');
    setResetDialogOpen(true);
  };

  const regenerateMutation = useMutation({
    mutationFn: (conversationId: number) => apiService.regenerateDeliveryLabel(conversationId),
    onSuccess: (res, conversationId) => {
      setRegenerateError(null);
      const tracking = res.trackingNumber ? ` — suivi ${res.trackingNumber}` : '';
      setRegenerateSuccessMessage(
        res.labelUrl
          ? `Bon régénéré pour la conversation #${conversationId}${tracking}.`
          : `Régénération terminée pour #${conversationId}${tracking || ''}.`
      );
      queryClient.invalidateQueries({ queryKey: ['delivery-transactions'] });
      if (res.labelUrl) {
        window.open(res.labelUrl, '_blank', 'noopener,noreferrer');
      }
      window.setTimeout(() => setRegenerateSuccessMessage(null), 6000);
    },
    onError: (err: unknown) => {
      const apiErr = err as { response?: { data?: { error?: string; message?: string } } };
      setRegenerateError(
        apiErr?.response?.data?.error ||
          apiErr?.response?.data?.message ||
          'Impossible de régénérer le bon de livraison.'
      );
    },
  });

  const transactions: DeliveryTransactionRow[] = data?.transactions ?? [];
  const pagination = data?.pagination;

  const timelineSteps = useMemo(() => {
    const raw = (trackingQuery.data?.steps ?? []) as Array<{
      status?: string | null;
      label?: string | null;
      date?: string | null;
      location?: string | null;
      isActive?: boolean;
    }>;

    const deduped: typeof raw = [];
    const seen = new Set<string>();
    for (const step of raw) {
      const label = (step.label ?? '').trim().toLowerCase();
      const date = (step.date ?? '').trim();
      const location = (step.location ?? '').trim().toLowerCase();
      const key = `${label}|${date}|${location}`;
      if (key === '||' || seen.has(key)) continue;
      seen.add(key);
      deduped.push(step);
    }

    const dated: Array<{ step: (typeof raw)[number]; ts: number }> = [];
    const undated: typeof raw = [];
    for (const step of deduped) {
      const date = (step.date ?? '').trim();
      const ts = date ? Date.parse(date) : Number.NaN;
      if (!Number.isNaN(ts)) {
        dated.push({ step, ts });
      } else {
        undated.push(step);
      }
    }

    if (dated.length === 0) {
      return deduped;
    }

    dated.sort((a, b) => a.ts - b.ts);
    const pending = undated.filter((step) => {
      const label = (step.label ?? '').trim().toLowerCase();
      return (
        label.includes('bientôt')
        || label.includes('bientot')
        || label.includes('préparé chez')
        || label.includes('prepare chez')
        || label.includes('va nous être confié')
        || label.includes('va nous etre confie')
      );
    });

    return [...pending, ...dated.map((entry) => entry.step)];
  }, [trackingQuery.data?.steps]);

  const formatName = (u: { first_name?: string; last_name?: string }) => {
    const first = u?.first_name ?? '';
    const last = u?.last_name ?? '';
    return `${first} ${last}`.trim() || '—';
  };

  const formatShippingSummary = (t: DeliveryTransactionRow) => {
    const phone = t.shipping?.phone || t.payer?.phone || null;
    const pickup = t.shipping?.pickup_point || null;
    const address = t.shipping?.address || null;
    const lines: string[] = [];
    if (pickup) lines.push(`Relais : ${pickup}`);
    else if (address) lines.push(address);
    if (phone) lines.push(`Tél. ${phone}`);
    return lines;
  };

  const stageChip = (stage: DeliveryTransactionRow['stage'], refundStatus?: DeliveryTransactionRow['refund_status']) => {
    if (refundStatus === 'full') {
      return <Chip label="Remboursée" color="default" size="small" />;
    }
    if (refundStatus === 'partial') {
      return <Chip label="Remboursée (partiel)" color="warning" size="small" />;
    }
    switch (stage) {
      case 'offer':
        return <Chip label="Proposée" color="info" size="small" />;
      case 'checkout_pending':
        return <Chip label="Checkout en cours" color="warning" size="small" />;
      case 'paid':
        return <Chip label="Payée" color="success" size="small" />;
      case 'failed':
        return <Chip label="Échec paiement" color="error" size="small" />;
      default:
        return <Chip label="Inconnu" size="small" />;
    }
  };

  const formatEUR = (amount: number | null | undefined) => {
    if (amount == null || Number.isNaN(amount)) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatObjectNumber = (t: DeliveryTransactionRow) => {
    const ref = t.found_object?.reference_code?.trim();
    if (ref) return ref;
    if (t.found_object?.id) return `#${t.found_object.id}`;
    return '—';
  };

  const formatAmountCell = (t: DeliveryTransactionRow) => {
    if (t.paid_amount_eur != null) {
      return formatEUR(t.paid_amount_eur);
    }
    if (t.proposed_amount_eur != null) {
      return formatEUR(t.proposed_amount_eur);
    }
    if (t.amount_eur != null) {
      return formatEUR(t.amount_eur);
    }
    return '—';
  };

  const parcelStatusChip = (t: DeliveryTransactionRow) => {
    const status = t.parcel_status ?? (t.tracking ? 'unknown' : 'no_tracking');

    switch (status) {
      case 'delivered':
        return (
          <Box>
            <Chip label="Livré" color="success" size="small" />
            {t.delivered_at ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t.delivered_at}
              </Typography>
            ) : null}
          </Box>
        );
      case 'returned':
        return (
          <Box>
            <Chip label="Retourné" color="error" size="small" />
            {t.returned_at ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t.returned_at}
              </Typography>
            ) : null}
          </Box>
        );
      case 'in_transit':
        return <Chip label="En transit" color="info" size="small" />;
      case 'pending':
        return <Chip label="En attente" color="default" size="small" variant="outlined" />;
      case 'no_tracking':
        return <Typography variant="body2" color="text.secondary">Sans n°</Typography>;
      case 'unknown':
        return <Chip label="Inconnu" size="small" color="default" />;
      default:
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
  };

  const submitTrackingSearch = () => {
    setTrackingSearch(trackingSearchInput.trim().toUpperCase().replace(/[^A-Z0-9]/g, ''));
    setPage(1);
  };

  const canRefund = (t: DeliveryTransactionRow) =>
    Boolean(t.stripe_session_id) && t.has_payment && t.refund_status !== 'full';

  const canRegenerateLabel = (t: DeliveryTransactionRow) =>
    Boolean(t.stripe_session_id) && (t.has_payment || t.stage === 'paid');

  const canEditProposedAmount = (t: DeliveryTransactionRow) =>
    t.has_offer && !t.has_payment && (t.stage === 'offer' || t.stage === 'checkout_pending');

  const openAmountDialog = (transaction: DeliveryTransactionRow) => {
    setAmountTarget(transaction);
    const current =
      transaction.proposed_amount_eur ??
      transaction.amount_eur ??
      18;
    setAmountEurInput(Number.isInteger(current) ? String(current) : current.toFixed(2));
    setAmountError(null);
    setAmountSuccess(null);
    setAmountDialogOpen(true);
  };

  const updateAmountMutation = useMutation({
    mutationFn: ({ conversationId, amountEur }: { conversationId: number; amountEur: number }) =>
      apiService.updateDeliveryProposedAmount(conversationId, amountEur),
    onSuccess: (res) => {
      setAmountError(null);
      const cleared = res.result.pending_checkouts_cleared;
      setAmountSuccess(
        cleared > 0
          ? `Tarif mis à jour (${res.result.proposed_amount_eur} €). ${cleared} checkout Stripe en attente annulé.`
          : `Tarif mis à jour : ${res.result.proposed_amount_eur} €.`
      );
      queryClient.invalidateQueries({ queryKey: ['delivery-transactions'] });
      window.setTimeout(() => {
        setAmountDialogOpen(false);
        setAmountSuccess(null);
        setAmountTarget(null);
      }, 1200);
    },
    onError: (err: unknown) => {
      const apiErr = err as { response?: { data?: { error?: string } } };
      setAmountSuccess(null);
      setAmountError(apiErr?.response?.data?.error || 'Impossible de modifier le tarif.');
    },
  });

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Tableau Stripe
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Toutes les livraisons en cours ou terminées : proposition trouveur, checkout Stripe et paiements
        (Colissimo, Chronopost, Mondial Relay). Remboursements Stripe possibles depuis cette page (exceptions encadrées).
      </Typography>

      {refundSuccessMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRefundSuccessMessage(null)}>
          {refundSuccessMessage}
        </Alert>
      ) : null}
      {regenerateSuccessMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRegenerateSuccessMessage(null)}>
          {regenerateSuccessMessage}
        </Alert>
      ) : null}
      {regenerateError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRegenerateError(null)}>
          {regenerateError}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Mode Stripe (test / production)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Les clés restent dans le <code>.env</code> de l&apos;API. Ce réglage choisit quel jeu de clés est
          utilisé pour les checkouts et le webhook.
        </Typography>

        {stripeSettingsQuery.isLoading ? (
          <CircularProgress size={24} />
        ) : stripeSettingsQuery.error ? (
          <Alert severity="error">Impossible de charger le mode Stripe.</Alert>
        ) : (
          <>
            {stripeSettingsMessage && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStripeSettingsMessage(null)}>
                {stripeSettingsMessage}
              </Alert>
            )}
            {stripeSettingsError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStripeSettingsError(null)}>
                {stripeSettingsError}
              </Alert>
            )}

            <FormControl>
              <RadioGroup
                row
                value={stripeMode}
                onChange={(e) => setStripeMode(e.target.value as 'test' | 'live')}
              >
                <FormControlLabel
                  value="test"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Test</span>
                      <Chip
                        size="small"
                        label={stripeSettings?.test.configured ? 'Clés OK' : 'Clés manquantes'}
                        color={stripeSettings?.test.configured ? 'success' : 'warning'}
                      />
                    </Box>
                  }
                />
                <FormControlLabel
                  value="live"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Production (live)</span>
                      <Chip
                        size="small"
                        label={stripeSettings?.live.configured ? 'Clés OK' : 'Clés manquantes'}
                        color={stripeSettings?.live.configured ? 'success' : 'warning'}
                      />
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Box sx={{ mt: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Test — clé secrète
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {stripeSettings?.test.secret_key_hint ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Test — webhook
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {stripeSettings?.test.webhook_secret_hint ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Live — clé secrète
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {stripeSettings?.live.secret_key_hint ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Live — webhook
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {stripeSettings?.live.webhook_secret_hint ?? '—'}
                </Typography>
              </Box>
            </Box>

            {stripeMode === 'live' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                En mode production, les paiements sont réels. Vérifiez que le webhook Stripe live pointe vers
                l&apos;API avec le secret <code>STRIPE_LIVE_WEBHOOK_SECRET</code>.
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                disabled={
                  stripeSettingsMutation.isPending
                  || stripeMode === stripeSettings?.mode
                  || (stripeMode === 'test' && !stripeSettings?.test.configured)
                  || (stripeMode === 'live' && !stripeSettings?.live.configured)
                }
                onClick={() => stripeSettingsMutation.mutate(stripeMode)}
                sx={{ textTransform: 'none' }}
              >
                {stripeSettingsMutation.isPending ? 'Enregistrement…' : 'Appliquer le mode'}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {refundSuccessMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRefundSuccessMessage(null)}>
          {refundSuccessMessage}
        </Alert>
      ) : null}

      {resetSuccessMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResetSuccessMessage(null)}>
          {resetSuccessMessage}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Réinitialiser un processus livraison (test)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Supprime les messages de livraison d&apos;une conversation pour refaire le parcours trouveur →
          paiement → étiquette. Indiquez l&apos;ID de conversation (visible dans le détail d&apos;un paiement).
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            label="ID conversation"
            type="number"
            value={resetConversationId}
            onChange={(e) => setResetConversationId(e.target.value)}
            sx={{ width: 180 }}
          />
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestartAltIcon />}
            disabled={!resetConversationId.trim()}
            onClick={() => {
              const id = Number.parseInt(resetConversationId.trim(), 10);
              if (Number.isFinite(id) && id > 0) openResetDialog(id);
            }}
            sx={{ textTransform: 'none' }}
          >
            Préparer la réinitialisation…
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les transactions livraison.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Rechercher un n° de colis"
              placeholder="Ex. 8R12345678901"
              value={trackingSearchInput}
              onChange={(e) => setTrackingSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitTrackingSearch();
              }}
              sx={{ minWidth: 260, flex: 1 }}
            />
            <Button variant="contained" onClick={submitTrackingSearch} sx={{ textTransform: 'none' }}>
              Rechercher
            </Button>
            {trackingSearch ? (
              <Button
                variant="text"
                onClick={() => {
                  setTrackingSearchInput('');
                  setTrackingSearch('');
                  setPage(1);
                }}
                sx={{ textTransform: 'none' }}
              >
                Effacer
              </Button>
            ) : null}
            {isFetching && !isLoading ? <CircularProgress size={22} /> : null}
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Statut colis / paiement</InputLabel>
              <Select
                label="Statut colis / paiement"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="delivered">Livré</MenuItem>
                <MenuItem value="returned">Retourné</MenuItem>
                <MenuItem value="in_transit">En transit</MenuItem>
                <MenuItem value="pending">En attente (Colissimo)</MenuItem>
                <MenuItem value="no_tracking">Sans n° de colis</MenuItem>
                <MenuItem value="unknown">Suivi inconnu</MenuItem>
                <MenuItem value="refunded">Remboursé</MenuItem>
                <MenuItem value="offer">Proposition (non payée)</MenuItem>
                <MenuItem value="checkout_pending">Checkout en cours</MenuItem>
                <MenuItem value="payment_failed">Échec paiement / étiquette</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Conv.</TableCell>
                  <TableCell>N° objet</TableCell>
                  <TableCell>Montant</TableCell>
                  <TableCell>Perdu (payer)</TableCell>
                  <TableCell>Adresse / tél.</TableCell>
                  <TableCell>Trouvé (finder)</TableCell>
                  <TableCell>Annonce</TableCell>
                  <TableCell>N° colis</TableCell>
                  <TableCell>Colis</TableCell>
                  <TableCell>Paiement</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        Aucune transaction livraison trouvée.
                      </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((t) => (
                    <TableRow
                      key={t.conversation_id}
                      hover
                      onClick={() => {
                        setSelectedTransaction(t);
                        setDetailsDialogOpen(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{t.created_at}</TableCell>
                      <TableCell>#{t.conversation_id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {formatObjectNumber(t)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatAmountCell(t)}
                          </Typography>
                          {t.paid_amount_eur == null && t.proposed_amount_eur != null ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Proposé
                            </Typography>
                          ) : null}
                          {t.paid_amount_eur != null
                            && t.proposed_amount_eur != null
                            && t.paid_amount_eur !== t.proposed_amount_eur ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Prop. {formatEUR(t.proposed_amount_eur)}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>{formatName(t.payer)}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {formatShippingSummary(t).length > 0 ? (
                          <Box>
                            {formatShippingSummary(t).map((line) => (
                              <Typography
                                key={line}
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ lineHeight: 1.35 }}
                              >
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{formatName(t.finder)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {t.announcement_thumbnail_url ? (
                            <Box
                              component="img"
                              src={t.announcement_thumbnail_url}
                              alt="Annonce"
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 1,
                                objectFit: 'cover',
                                flexShrink: 0,
                                border: '1px solid rgba(0,0,0,0.08)',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          ) : null}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {t.announcement?.title ?? '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {t.announcement.type ? t.announcement.type : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {t.tracking ? (
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {t.tracking}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{parcelStatusChip(t)}</TableCell>
                      <TableCell>{stageChip(t.stage, t.refund_status)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {canEditProposedAmount(t) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              disabled={updateAmountMutation.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                openAmountDialog(t);
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Tarif
                            </Button>
                          ) : null}
                          {canRefund(t) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<MoneyOffIcon />}
                              disabled={refundMutation.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                openRefundDialog(t);
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Rembourser
                            </Button>
                          ) : null}
                          {canRegenerateLabel(t) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ReplayIcon />}
                              disabled={regenerateMutation.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRegenerateError(null);
                                if (
                                  window.confirm(
                                    `Régénérer le bon de livraison pour la conversation #${t.conversation_id} ?`
                                  )
                                ) {
                                  regenerateMutation.mutate(t.conversation_id);
                                }
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Régénérer
                            </Button>
                          ) : null}
                          {t.tracking ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LocalShippingIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTransaction(t);
                                setTrackingToShow(t.tracking);
                                setTrackingDialogOpen(true);
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Suivi
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<DeleteOutlineIcon />}
                            disabled={resetMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              openResetDialog(t.conversation_id);
                            }}
                            sx={{ textTransform: 'none' }}
                          >
                            Supprimer
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={limit}
              onRowsPerPageChange={(e) => {
                const next = Number(e.target.value);
                setLimit(next);
                setPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          )}
        </Paper>
      )}

      <Dialog
        open={trackingDialogOpen}
        onClose={() => {
          setTrackingDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Suivi Colissimo</DialogTitle>
        <DialogContent>
          {!trackingToShow ? (
            <Alert severity="warning">Tracking manquant.</Alert>
          ) : trackingQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : trackingQuery.isError ? (
            <Alert severity="error">
              {trackingQuery.error ? String(trackingQuery.error) : 'Erreur Colissimo'}
            </Alert>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: 'monospace' }}>
                {trackingToShow}
              </Typography>

              {trackingQuery.data?.is_returned === true ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Colis retourné à l&apos;expéditeur
                  {trackingQuery.data.returned_at ? ` — ${trackingQuery.data.returned_at}` : ''}
                  {trackingQuery.data.return_label ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {trackingQuery.data.return_label}
                    </Typography>
                  ) : null}
                </Alert>
              ) : trackingQuery.data?.is_returned === false ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Aucun retour expéditeur détecté dans le suivi Colissimo.
                </Alert>
              ) : null}

              {timelineSteps.length === 0 ? (
                <Alert severity="info">Aucun événement retourné.</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {timelineSteps.map((s, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: s.isActive ? 'rgba(67,160,71,0.12)' : 'transparent',
                        border: s.isActive ? '1px solid rgba(67,160,71,0.35)' : '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <Box
                        sx={{
                          mt: 0.35,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: s.isActive ? '#43a047' : '#bdbdbd',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: s.isActive ? 700 : 600 }}>
                          {s.label ?? s.status ?? `Etape ${idx + 1}`}
                        </Typography>
                        {(s.location || s.date) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                            {[s.location, s.date].filter(Boolean).join(' - ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Détails transaction livraison</DialogTitle>
        <DialogContent>
          {!selectedTransaction ? (
            <Alert severity="warning">Aucune transaction sélectionnée.</Alert>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                {selectedTransaction.announcement_thumbnail_url ? (
                  <Box
                    component="img"
                    src={selectedTransaction.announcement_thumbnail_url}
                    alt="Annonce"
                    sx={{
                      width: 86,
                      height: 86,
                      borderRadius: 2,
                      objectFit: 'cover',
                      border: '1px solid rgba(0,0,0,0.08)',
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {selectedTransaction.announcement?.title ?? '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedTransaction.announcement?.type ? selectedTransaction.announcement.type : ''}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {stageChip(selectedTransaction.stage, selectedTransaction.refund_status)}
                    {selectedTransaction.refunded_amount_eur != null ? (
                      <Chip
                        size="small"
                        label={`Remboursé : ${formatEUR(selectedTransaction.refunded_amount_eur)}`}
                        color="warning"
                      />
                    ) : null}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <Chip label={`Conversation: ${selectedTransaction.conversation_id}`} />
                <Chip label={`N° objet: ${formatObjectNumber(selectedTransaction)}`} />
                {selectedTransaction.paid_amount_eur != null ? (
                  <Chip label={`Payé: ${formatEUR(selectedTransaction.paid_amount_eur)}`} color="success" />
                ) : null}
                {selectedTransaction.proposed_amount_eur != null ? (
                  <Chip label={`Proposé: ${formatEUR(selectedTransaction.proposed_amount_eur)}`} color="info" />
                ) : selectedTransaction.amount_eur != null ? (
                  <Chip label={`Montant: ${formatEUR(selectedTransaction.amount_eur)}`} />
                ) : null}
                <Chip
                  label={
                    selectedTransaction.stripe_session_id
                      ? `Stripe: ${selectedTransaction.stripe_session_id}`
                      : 'Stripe: —'
                  }
                  sx={{ maxWidth: '100%' }}
                />
                {selectedTransaction.pending_checkout_count > 0 ? (
                  <Chip
                    label={`${selectedTransaction.pending_checkout_count} checkout en attente`}
                    color="warning"
                  />
                ) : null}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Perdu (payer)
                  </Typography>
                  <Typography variant="body2">{formatName(selectedTransaction.payer)}</Typography>
                  {selectedTransaction.payer.email ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {selectedTransaction.payer.email}
                    </Typography>
                  ) : null}
                  {(selectedTransaction.shipping?.phone || selectedTransaction.payer.phone) ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Tél. {selectedTransaction.shipping?.phone || selectedTransaction.payer.phone}
                    </Typography>
                  ) : null}
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Trouvé (finder)
                  </Typography>
                  <Typography variant="body2">{formatName(selectedTransaction.finder)}</Typography>
                  {selectedTransaction.finder.email ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {selectedTransaction.finder.email}
                    </Typography>
                  ) : null}
                </Paper>
              </Box>

              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Adresse / livraison
                </Typography>
                {selectedTransaction.shipping?.mode === 'pickup_point' ||
                selectedTransaction.shipping?.pickup_point ? (
                  <Typography variant="body2">
                    Point relais : {selectedTransaction.shipping?.pickup_point ?? '—'}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    {selectedTransaction.shipping?.address ||
                      [
                        selectedTransaction.payer.address,
                        [selectedTransaction.payer.postal_code, selectedTransaction.payer.city]
                          .filter(Boolean)
                          .join(' '),
                      ]
                        .filter(Boolean)
                        .join(', ') ||
                      '—'}
                  </Typography>
                )}
                {(selectedTransaction.shipping?.phone || selectedTransaction.payer.phone) ? (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Téléphone : {selectedTransaction.shipping?.phone || selectedTransaction.payer.phone}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Téléphone non renseigné
                  </Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Livraison / suivi
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  N° colis : {selectedTransaction.tracking ?? '—'}
                </Typography>
                {selectedTransaction.tracking ? (
                  <Box sx={{ mt: 1 }}>
                    {parcelStatusChip(selectedTransaction)}
                    {selectedTransaction.return_label ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {selectedTransaction.return_label}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {selectedTransaction.etiquette_url ? (
                    <Button
                      variant="contained"
                      href={selectedTransaction.etiquette_url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      Etiquette
                    </Button>
                  ) : null}
                  {selectedTransaction.tracking ? (
                    <Button
                      variant="outlined"
                      startIcon={<LocalShippingIcon />}
                      onClick={() => {
                        setTrackingToShow(selectedTransaction.tracking);
                        setTrackingDialogOpen(true);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Ouvrir suivi
                    </Button>
                  ) : null}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedTransaction && canEditProposedAmount(selectedTransaction) ? (
            <Button
              startIcon={<EditIcon />}
              disabled={updateAmountMutation.isPending}
              onClick={() => {
                setDetailsDialogOpen(false);
                openAmountDialog(selectedTransaction);
              }}
              sx={{ textTransform: 'none', mr: 1 }}
            >
              Modifier le tarif
            </Button>
          ) : null}
          {selectedTransaction && canRefund(selectedTransaction) ? (
            <Button
              color="secondary"
              startIcon={<MoneyOffIcon />}
              disabled={refundMutation.isPending}
              onClick={() => {
                setDetailsDialogOpen(false);
                openRefundDialog(selectedTransaction);
              }}
              sx={{ textTransform: 'none', mr: 1 }}
            >
              Rembourser sur Stripe
            </Button>
          ) : null}
          {selectedTransaction && canRegenerateLabel(selectedTransaction) ? (
            <Button
              startIcon={<ReplayIcon />}
              disabled={regenerateMutation.isPending}
              onClick={() => {
                setRegenerateError(null);
                if (
                  window.confirm(
                    `Régénérer le bon de livraison pour la conversation #${selectedTransaction.conversation_id} ?`
                  )
                ) {
                  regenerateMutation.mutate(selectedTransaction.conversation_id);
                }
              }}
              sx={{ textTransform: 'none', mr: 1 }}
            >
              {regenerateMutation.isPending ? 'Régénération…' : 'Régénérer le bon'}
            </Button>
          ) : null}
          {selectedTransaction ? (
            <Button
              color="warning"
              startIcon={<RestartAltIcon />}
              onClick={() => {
                setDetailsDialogOpen(false);
                openResetDialog(selectedTransaction.conversation_id);
              }}
              sx={{ textTransform: 'none', mr: 'auto' }}
            >
              Supprimer le processus livraison
            </Button>
          ) : null}
          <Button onClick={() => setDetailsDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={amountDialogOpen}
        onClose={() => {
          if (!updateAmountMutation.isPending) {
            setAmountDialogOpen(false);
            setAmountTarget(null);
            setAmountError(null);
            setAmountSuccess(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Modifier le tarif proposé</DialogTitle>
        <DialogContent>
          {amountTarget ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Conversation #{amountTarget.conversation_id}
                {formatObjectNumber(amountTarget) !== '—'
                  ? ` — objet ${formatObjectNumber(amountTarget)}`
                  : ''}
              </Typography>
              <Alert severity="info">
                Possible uniquement tant que le propriétaire n&apos;a pas payé. Si un checkout Stripe était
                ouvert, il sera annulé pour forcer le nouveau montant.
              </Alert>
              <TextField
                label="Nouveau tarif (€)"
                value={amountEurInput}
                onChange={(e) => {
                  setAmountEurInput(e.target.value);
                  setAmountError(null);
                }}
                inputProps={{ inputMode: 'decimal', min: 1, step: 0.5 }}
                autoFocus
                fullWidth
              />
              {amountError ? <Alert severity="error">{amountError}</Alert> : null}
              {amountSuccess ? <Alert severity="success">{amountSuccess}</Alert> : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAmountDialogOpen(false);
              setAmountTarget(null);
            }}
            disabled={updateAmountMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            disabled={updateAmountMutation.isPending || !amountTarget}
            onClick={() => {
              if (!amountTarget) return;
              const normalized = amountEurInput.trim().replace(',', '.');
              const amountEur = Number(normalized);
              if (!Number.isFinite(amountEur) || amountEur < 1 || amountEur > 500) {
                setAmountError('Montant invalide (entre 1 € et 500 €).');
                return;
              }
              updateAmountMutation.mutate({
                conversationId: amountTarget.conversation_id,
                amountEur,
              });
            }}
            sx={{ textTransform: 'none' }}
          >
            {updateAmountMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetDialogOpen}
        onClose={() => {
          if (!resetMutation.isPending) {
            setResetDialogOpen(false);
            setResetTargetConversationId(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Réinitialiser le processus livraison</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Conversation #{resetTargetConversationId ?? '—'}
          </Typography>

          {resetStatusQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : resetStatusQuery.isError ? (
            <Alert severity="error">Conversation introuvable ou erreur de chargement.</Alert>
          ) : resetStatusQuery.data?.delivery ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label={resetStatusQuery.data.delivery.has_offer ? 'Proposition trouveur' : 'Pas de proposition'}
                  color={resetStatusQuery.data.delivery.has_offer ? 'info' : 'default'}
                />
                <Chip
                  size="small"
                  label={resetStatusQuery.data.delivery.has_payment ? 'Paiement enregistré' : 'Pas de paiement'}
                  color={resetStatusQuery.data.delivery.has_payment ? 'success' : 'default'}
                />
                {resetStatusQuery.data.delivery.pending_checkout_count > 0 ? (
                  <Chip
                    size="small"
                    label={`${resetStatusQuery.data.delivery.pending_checkout_count} checkout en attente`}
                    color="warning"
                  />
                ) : null}
                {resetStatusQuery.data.delivery.found_object ? (
                  <Chip
                    size="small"
                    label={`Objet trouvé #${resetStatusQuery.data.delivery.found_object.id} — ${resetStatusQuery.data.delivery.found_object.status}`}
                  />
                ) : null}
              </Box>
              {!resetStatusQuery.data.delivery.announcement_active ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  L&apos;annonce liée est inactive : la messagerie peut rester en lecture seule même après
                  réinitialisation.
                </Alert>
              ) : null}
            </Box>
          ) : null}

          <FormControl component="fieldset">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Portée de la réinitialisation
            </Typography>
            <RadioGroup
              value={resetScope}
              onChange={(e) => setResetScope(e.target.value as 'full' | 'payment_only')}
            >
              <FormControlLabel
                value="full"
                control={<Radio size="small" />}
                label="Complète — proposition trouveur + paiement + checkouts en attente (repartir de zéro)"
              />
              <FormControlLabel
                value="payment_only"
                control={<Radio size="small" />}
                label="Paiement seulement — garde la proposition du trouveur, permet de repayer"
              />
            </RadioGroup>
          </FormControl>

          {resetMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(resetMutation.error as Error)?.message || 'Échec de la réinitialisation.'}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResetDialogOpen(false);
              setResetTargetConversationId(null);
            }}
            disabled={resetMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={
              resetMutation.isPending ||
              !resetTargetConversationId ||
              resetStatusQuery.isLoading ||
              resetStatusQuery.isError
            }
            onClick={() => {
              if (!resetTargetConversationId) return;
              resetMutation.mutate({
                conversationId: resetTargetConversationId,
                scope: resetScope,
              });
            }}
            sx={{ textTransform: 'none' }}
          >
            {resetMutation.isPending ? 'Réinitialisation…' : 'Confirmer la réinitialisation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={refundDialogOpen}
        onClose={() => {
          if (!refundMutation.isPending) {
            setRefundDialogOpen(false);
            setRefundTarget(null);
            setRefundError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rembourser sur Stripe</DialogTitle>
        <DialogContent>
          {refundTarget ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Conversation #{refundTarget.conversation_id} — {formatName(refundTarget.payer)}
              </Typography>
              <Alert severity="info">
                Remboursement total de{' '}
                <strong>{formatEUR(refundTarget.paid_amount_eur ?? refundTarget.amount_eur)}</strong> sur la session{' '}
                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {refundTarget.stripe_session_id}
                </Typography>
                .
              </Alert>
              <Typography variant="body2" color="text.secondary">
                L’opération est irréversible côté Stripe. Le perdeur sera recrédité sous quelques jours ouvrés
                selon sa banque. À réserver aux cas exceptionnels (erreur, litige, annulation avant expédition).
              </Typography>
              {refundTarget.refund_status === 'partial' && refundTarget.refunded_amount_eur != null ? (
                <Alert severity="warning">
                  Un remboursement partiel de {formatEUR(refundTarget.refunded_amount_eur)} a déjà été enregistré.
                </Alert>
              ) : null}
              {refundError ? <Alert severity="error">{refundError}</Alert> : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRefundDialogOpen(false);
              setRefundTarget(null);
              setRefundError(null);
            }}
            disabled={refundMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            Annuler
          </Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={refundMutation.isPending || !refundTarget}
            onClick={() => {
              if (!refundTarget) return;
              refundMutation.mutate({ conversationId: refundTarget.conversation_id });
            }}
            sx={{ textTransform: 'none' }}
          >
            {refundMutation.isPending ? 'Remboursement…' : 'Confirmer le remboursement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

